import { useRef, useState } from "react";
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { throttle } from "lodash";
import useDeepCompareEffect from "use-deep-compare-effect";

export type AsyncRequestFunction<T, R extends unknown[]> = (...args: [...R, AbortSignal]) => Promise<T>;

export interface AsyncResponse<T> {
  loading: boolean;
  refresh: () => void;
  data?: T;
  error?: Error;
}

function useAsyncAbortableRequest<T, R extends unknown[]>(
  func: AsyncRequestFunction<T, R>,
  ...params: R
): AsyncResponse<T> {
  const { inProgress } = useMsal();

  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);

  // throttles api calls e.g. only 1 api call will be made within the set delay
  const throttled = useRef(
    throttle((isCleaningUp, signal: AbortSignal, params: R) => {
      setLoading(true);
      setError(undefined);
      func(...params, signal)
        .then((result) => {
          if (!isCleaningUp) {
            setData(result);
          }
        })
        .catch((ex) => {
          if (ex instanceof Error) {
            if (ex.name === "CanceledError") {
              // request was cancelled, ignore it
              return;
            }
          }
          if (!isCleaningUp) {
            setError(ex);
          }
        })
        .finally(() => {
          if (!isCleaningUp) {
            setLoading(false);
          }
        });
    }, 1000)
  );

  useDeepCompareEffect(() => {
    let isCleaningUp = false;

    const controller = new AbortController();
    controllerRef.current = controller;
    const signal = controllerRef.current.signal;

    if (inProgress === InteractionStatus.None) {
      throttled.current(isCleaningUp, signal, params);
    }

    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      isCleaningUp = true;
    };
  }, [func, inProgress, params, shouldRefresh]);

  return {
    data,
    refresh: () => setShouldRefresh(!shouldRefresh),
    loading,
    error,
  };
}

export default useAsyncAbortableRequest;
